import React, { useState, useRef, useCallback, useEffect } from "react";
import {
  X, Upload, Download, ChevronRight, ChevronLeft, Building2, Shield,
  AlertCircle, CheckCircle2, Loader2, FileText, Users, UserCheck,
  UserX, Clock, RefreshCw,
} from "lucide-react";
import clientService from "../../../services/client.service";
import userService from "../../../services/user.service";
import styles from "./BulkUserUploadModal.module.css";
import * as XLSX from "xlsx";

interface BulkUserUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (result: { created: number; failed: number }) => void;
  roles: Array<{ id: string; name: string }>;
  currentUser: any;
}

interface ParsedRow {
  "Email ID": string;
  "First Name": string;
  "Last Name": string;
  "Emp ID": string;
  "Date of Birth": string;
  "Phone Number": string;
  "Role Name"?: string;
  "Supervisor Email"?: string;
  email: string;
  first_name: string;
  last_name: string;
  emp_id: string;
  date_of_birth: string;
  phone_number: string;
  role_names?: string;
  supervisor_email?: string;
  _rowIndex: number;
  _errors: string[];
  _valid: boolean;
  [key: string]: any;
}

interface ClientOption {
  id: string;
  name: string;
  npi?: string;
  type?: string;
}

interface HistoryItem {
  id: string;
  created_at: string;
  original_filename: string;
  file_size_bytes: number | null;
  user_type: string;
  client_id: string | null;
  client_name: string | null;
  s3_key: string | null;
  status: "pending" | "uploaded" | "processing" | "completed" | "failed";
  total_rows: number | null;
  success_count: number | null;
  failed_count: number | null;
  errors: string[] | null;
}

interface RowResult {
  row_index: number;
  email?: string;
  status: "created" | "mapped" | "skipped" | "failed";
  reason?: string;
}

interface UploadResult {
  created: number;
  failed: number;
  mapped: number;
  skipped: number;
  rowResults: RowResult[];
  errors: string[];
}

const BASE_REQUIRED_FIELDS = ["Email ID", "First Name", "Last Name", "Emp ID", "DOB", "Phone Number"];
const INTERNAL_ONLY_FIELDS = ["Role Name"];
const ALL_REQUIRED_FIELDS = [...BASE_REQUIRED_FIELDS, ...INTERNAL_ONLY_FIELDS];

const CANONICAL_HEADERS = [
  "Emp ID", "First Name", "Last Name", "DOB", "Email ID",
  "Phone Number", "Supervisor Email", "Role Name",
];

const HEADER_MAP: Record<string, string> = {
  "Email ID": "email",
  "First Name": "first_name",
  "Last Name": "last_name",
  "Emp ID": "emp_id",
  DOB: "date_of_birth",
  "Phone Number": "phone_number",
  "Role Name": "role_names",
  "Supervisor Email": "supervisor_email",
};

const INTERNAL_TEMPLATE_HEADERS =
  "Emp ID,First Name,Last Name,DOB,Email ID,Phone Number,Supervisor Email,Role Name";
const INTERNAL_TEMPLATE_EXAMPLE =
  "\nEMP001,John,Doe,1990-05-15,john.doe@company.com,5551234567,supervisor@company.com,AGENT\nEMP002,Jane,Smith,1988-03-22,jane.smith@company.com,5559876543,,AGENT";
const CLIENT_TEMPLATE_HEADERS = "Emp ID,First Name,Last Name,DOB,Email ID,Phone Number";
const CLIENT_TEMPLATE_EXAMPLE =
  "\nEMP003,Alice,Walker,1992-07-10,alice.walker@client.com,5551111111\nEMP004,Bob,Carter,1985-11-30,bob.carter@client.com,5552222222";

const STATUS_META: Record<HistoryItem["status"], { label: string; color: string }> = {
  pending:    { label: "Pending",    color: "#888"    },
  uploaded:   { label: "Uploaded",   color: "#3B82F6" },
  processing: { label: "Processing", color: "#F59E0B" },
  completed:  { label: "Completed",  color: "#10B981" },
  failed:     { label: "Failed",     color: "#EF4444" },
};

function fmtBytes(b: number | null): string {
  if (!b) return "—";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
}

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

function toSafeString(val: any): string {
  return typeof val === "string" ? val.trim() : String(val ?? "").trim();
}

function buildNormalizeMap(): Record<string, string> {
  const map: Record<string, string> = {};
  CANONICAL_HEADERS.forEach((h) => { map[h.toLowerCase().trim()] = h; });
  return map;
}
const NORMALIZE_MAP = buildNormalizeMap();

function remapToCanonical(rawKey: string): string {
  return NORMALIZE_MAP[rawKey.toLowerCase().trim()] ?? rawKey;
}

function parseMonthName(mon: string): string {
  const months: Record<string, string> = {
    jan: "01", feb: "02", mar: "03", apr: "04", may: "05", jun: "06",
    jul: "07", aug: "08", sep: "09", oct: "10", nov: "11", dec: "12",
    januray: "01", janury: "01",
  };
  const key3 = mon.toLowerCase().slice(0, 3);
  return months[key3] ?? months[mon.toLowerCase()] ?? "";
}

function normalizeDOB(val: any): string {
  if (!val && val !== 0) return "";
  if (typeof val === "number") {
    const d = new Date((val - 25569) * 86400 * 1000);
    return d.toISOString().split("T")[0];
  }
  if (val instanceof Date) return val.toISOString().split("T")[0];
  const s = String(val).trim();
  if (!s) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const slashParts = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (slashParts) {
    const [, a, b, y] = slashParts;
    return parseInt(b, 10) > 12
      ? `${y}-${a.padStart(2, "0")}-${b.padStart(2, "0")}`
      : `${y}-${b.padStart(2, "0")}-${a.padStart(2, "0")}`;
  }
  const dMonYY = s.match(/^(\d{1,2})-([A-Za-z]+)-(\d{2,4})$/);
  if (dMonYY) {
    const [, d, mon, y] = dMonYY;
    const month = parseMonthName(mon);
    if (!month) return "";
    const year = y.length === 2 ? (parseInt(y, 10) > 30 ? `19${y}` : `20${y}`) : y;
    return `${year}-${month}-${d.padStart(2, "0")}`;
  }
  const monDY = s.match(/^([A-Za-z]+)-(\d{1,2})-(\d{4})$/);
  if (monDY) {
    const [, mon, d, y] = monDY;
    const month = parseMonthName(mon);
    if (!month) return "";
    return `${y}-${month}-${d.padStart(2, "0")}`;
  }
  const monDYLong = s.match(/^([A-Za-z]+)\s+(\d{1,2}),?\s+(\d{4})$/);
  if (monDYLong) {
    const [, mon, d, y] = monDYLong;
    const month = parseMonthName(mon);
    if (!month) return "";
    return `${y}-${month}-${d.padStart(2, "0")}`;
  }
  const dmyDash = s.match(/^(\d{1,2})-(\d{2})-(\d{4})$/);
  if (dmyDash) {
    const [, d, m, y] = dmyDash;
    return `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`;
  }
  const fallback = new Date(s);
  if (!isNaN(fallback.getTime())) return fallback.toISOString().split("T")[0];
  return "";
}

function buildCanonicalRow(rawObj: Record<string, any>, index: number): Record<string, any> {
  const row: Record<string, any> = {};
  Object.entries(rawObj).forEach(([rawKey, val]) => { row[remapToCanonical(rawKey)] = val; });
  row["DOB"] = normalizeDOB(row["DOB"]);
  row["Date of Birth"] = row["DOB"];
  Object.entries(HEADER_MAP).forEach(([csvKey, backendKey]) => {
    if (csvKey in row) row[backendKey] = row[csvKey];
  });
  row._rowIndex = String(index + 2);
  return row;
}

function parseCSV(text: string, isClient: boolean): { headers: string[]; rows: Record<string, any>[]; error: string | null } {
  const lines = text.trim().split(/\r?\n/).filter(Boolean);
  if (lines.length < 2) return { headers: [], rows: [], error: "CSV must have a header row and at least one data row." };
  const rawHeaders = lines[0].split(",").map((h) => h.trim().replace(/^"|"$/g, ""));
  const headers = rawHeaders.map(remapToCanonical);
  const required = isClient ? BASE_REQUIRED_FIELDS : ALL_REQUIRED_FIELDS;
  const missing = required.filter((f) => !headers.includes(f));
  if (missing.length > 0) return { headers, rows: [], error: `Missing required columns: ${missing.join(", ")}` };
  const rows = lines.slice(1).map((line, i) => {
    const values: string[] = [];
    let cur = "", inQuote = false;
    for (const ch of line) {
      if (ch === '"') { inQuote = !inQuote; continue; }
      if (ch === "," && !inQuote) { values.push(cur.trim()); cur = ""; continue; }
      cur += ch;
    }
    values.push(cur.trim());
    const rawObj: Record<string, string> = {};
    rawHeaders.forEach((h, idx) => { rawObj[h] = values[idx] ?? ""; });
    return buildCanonicalRow(rawObj, i);
  });
  return { headers, rows, error: null };
}

function parseXLSX(buffer: ArrayBuffer, isClient: boolean): { headers: string[]; rows: Record<string, any>[]; error: string | null } {
  const workbook = XLSX.read(new Uint8Array(buffer), { type: "array", cellDates: true });
  const sheet = workbook.Sheets[workbook.SheetNames[0]];
  const json = XLSX.utils.sheet_to_json<Record<string, any>>(sheet, { defval: "", raw: false, dateNF: "yyyy-mm-dd" });
  if (!json.length) return { headers: [], rows: [], error: "Excel file is empty." };
  const rawHeaders = Object.keys(json[0]);
  const headers = rawHeaders.map(remapToCanonical);
  const required = isClient ? BASE_REQUIRED_FIELDS : ALL_REQUIRED_FIELDS;
  const missing = required.filter((f) => !headers.includes(f));
  if (missing.length > 0) return { headers, rows: [], error: `Missing required columns: ${missing.join(", ")}` };
  const rawToCanonical: Record<string, string> = {};
  rawHeaders.forEach((raw, i) => { rawToCanonical[raw] = headers[i]; });
  const rows = json.map((row, index) => {
    const reKeyed: Record<string, any> = {};
    Object.entries(row).forEach(([k, v]) => { reKeyed[rawToCanonical[k] ?? k] = v; });
    return buildCanonicalRow(reKeyed, index);
  });
  return { headers, rows, error: null };
}

function validateRows(rows: Record<string, any>[]): ParsedRow[] {
  const seenEmails = new Set<string>();
  const seenEmpIds = new Set<string>();
  return rows.map((row) => {
    const errors: string[] = [];
    BASE_REQUIRED_FIELDS.forEach((f) => {
      if (!row[f] || toSafeString(row[f]) === "") errors.push(`Missing: ${f}`);
    });
    const email = toSafeString(row["Email ID"]).toLowerCase();
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) errors.push("Invalid email format");
    const dob = toSafeString(row["DOB"]);
    if (dob) {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(dob)) errors.push("Invalid date format (expected YYYY-MM-DD)");
      else if (new Date(dob) >= new Date()) errors.push("Date of Birth must be in the past");
    }
    if (email) {
      if (seenEmails.has(email)) errors.push("Duplicate email in file");
      else seenEmails.add(email);
    }
    const empId = toSafeString(row["Emp ID"]).toLowerCase();
    if (empId) {
      if (seenEmpIds.has(empId)) errors.push("Duplicate Emp ID in file");
      else seenEmpIds.add(empId);
    }
    return { ...row, _rowIndex: parseInt(row._rowIndex || "0", 10), _errors: errors, _valid: errors.length === 0 } as ParsedRow;
  });
}

// ─── Result XLSX generator — driven entirely by backend row_results ───────────
function generateResultXLSX(
  validated: ParsedRow[],
  rowResults: RowResult[],
  isClient: boolean
): Blob {
  // Email-keyed map from backend authoritative results
  const resultMap = new Map<string, RowResult>();
  rowResults.forEach((r) => {
    if (r.email) resultMap.set(r.email.toLowerCase(), r);
  });

  const columnOrder = isClient
    ? ["Emp ID", "First Name", "Last Name", "DOB", "Email ID", "Phone Number"]
    : ["Emp ID", "First Name", "Last Name", "DOB", "Email ID", "Phone Number", "Supervisor Email", "Role Name"];

  const rows = validated.map((row) => {
    const email = toSafeString(row["Email ID"]).toLowerCase();
    let status: string;

    if (!row._valid) {
      // Client-side validation failure — never sent to backend
      status = `Failed: ${row._errors.join("; ")}`;
    } else {
      const r = resultMap.get(email);
      if (!r) {
        status = "Completed"; // fallback, should not happen
      } else if (r.status === "created") {
        status = "Created";
      } else if (r.status === "mapped") {
        status = "Mapped (Supervisor Updated)";
      } else if (r.status === "skipped") {
        status = `Skipped: ${r.reason ?? ""}`;
      } else if (r.status === "failed") {
        status = `Failed: ${r.reason ?? ""}`;
      } else {
        status = r.status;
      }
    }

    const out: Record<string, any> = {};
    columnOrder.forEach((col) => {
      const backendKey = HEADER_MAP[col];
      out[col] = toSafeString(row[col] ?? (backendKey ? row[backendKey] : "") ?? "");
    });
    out["Upload Status"] = status;
    return out;
  });

  const ws = XLSX.utils.json_to_sheet(rows, { header: [...columnOrder, "Upload Status"] });
  ws["!cols"] = [...columnOrder.map(() => ({ wch: 22 })), { wch: 50 }];
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Upload Results");
  const buf = XLSX.write(wb, { type: "array", bookType: "xlsx" });
  return new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
}

// ─── Component ────────────────────────────────────────────────────────────────
const BulkUserUploadModal: React.FC<BulkUserUploadModalProps> = ({ isOpen, onClose, onSuccess }) => {
  const [activeTab, setActiveTab] = useState<"upload" | "history">("upload");
  const [step, setStep] = useState(0);
  const [userType, setUserType] = useState<"internal" | "client" | null>(null);
  const [clients, setClients] = useState<ClientOption[]>([]);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [selectedClient, setSelectedClient] = useState<ClientOption | null>(null);
  const [clientSearch, setClientSearch] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [dragging, setDragging] = useState(false);
  const [parsed, setParsed] = useState<{ headers: string[]; rows: Record<string, any>[]; error: string | null } | null>(null);
  const [validated, setValidated] = useState<ParsedRow[] | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<string | null>(null);
  const [uploadResult, setUploadResult] = useState<UploadResult | null>(null);
  const [resultBlob, setResultBlob] = useState<Blob | null>(null);
  const [resultFilename, setResultFilename] = useState<string>("");
  const fileRef = useRef<HTMLInputElement>(null);

  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const isClientType = userType === "client";
  const steps = isClientType
    ? ["User Type", "Select Client", "Upload File", "Review"]
    : ["User Type", "Upload File", "Review"];
  const uploadStep = isClientType ? 2 : 1;
  const reviewStep = isClientType ? 3 : 2;
  const doneStep   = isClientType ? 4 : 3;
  const validCount   = validated?.filter((r) => r._valid).length ?? 0;
  const invalidCount = validated?.filter((r) => !r._valid).length ?? 0;

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    setHistoryError(null);
    try {
      const data = await userService.getUserImportHistory();
      setHistory(data.items ?? []);
      setHistoryTotal(data.total ?? 0);
    } catch (e: any) {
      setHistoryError(e?.message ?? "Failed to load history.");
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen && activeTab === "history") loadHistory();
  }, [isOpen, activeTab, loadHistory]);

  const resetUpload = () => {
    setStep(0); setUserType(null); setSelectedClient(null); setClientSearch("");
    setClients([]); setFile(null); setParsed(null); setValidated(null);
    setUploading(false); setUploadProgress(null); setUploadResult(null);
    setResultBlob(null); setResultFilename("");
  };

  const handleClose = () => { resetUpload(); setActiveTab("upload"); onClose(); };

  const loadClients = useCallback(async () => {
    setClientsLoading(true);
    try {
      const data = await clientService.getVisibleClients();
      setClients(data.map((c: any) => ({
        id: c.id,
        name: c.business_name || `${c.first_name ?? ""} ${c.last_name ?? ""}`.trim() || "Unnamed",
        npi: c.npi, type: c.type,
      })));
    } catch (e) { console.error("Failed to load clients", e); }
    finally { setClientsLoading(false); }
  }, []);

  const handleFile = useCallback((f: File) => {
    const isCSV  = f.name.toLowerCase().endsWith(".csv");
    const isXLSX = f.name.toLowerCase().endsWith(".xlsx");
    if (!isCSV && !isXLSX) {
      setParsed({ headers: [], rows: [], error: "Only .csv and .xlsx files are accepted." });
      setValidated(null);
      return;
    }
    setFile(f);
    if (isCSV) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = parseCSV(e.target?.result as string, isClientType);
        setParsed(result);
        setValidated(result.error ? null : validateRows(result.rows));
      };
      reader.readAsText(f);
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      const result = parseXLSX(e.target?.result as ArrayBuffer, isClientType);
      setParsed(result);
      setValidated(result.error ? null : validateRows(result.rows));
    };
    reader.readAsArrayBuffer(f);
  }, [isClientType]);

  const onDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault(); setDragging(false);
    const f = e.dataTransfer.files[0];
    if (f) handleFile(f);
  }, [handleFile]);

  const downloadTemplate = () => {
    const headers = isClientType ? CLIENT_TEMPLATE_HEADERS : INTERNAL_TEMPLATE_HEADERS;
    const example = isClientType ? CLIENT_TEMPLATE_EXAMPLE : INTERNAL_TEMPLATE_EXAMPLE;
    const blob = new Blob([headers + example], { type: "text/csv" });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement("a");
    a.href = url; a.download = `bulk_${userType}_users_template.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const goNext = async () => {
    if (step === 0 && isClientType) await loadClients();
    setStep((s) => s + 1);
  };

  const goBack = () => {
    if (step === uploadStep) { setFile(null); setParsed(null); setValidated(null); }
    setStep((s) => s - 1);
  };

  const handleSubmit = async () => {
    if (!validated || !userType || validated.some((r) => !r._valid)) return;
    setUploading(true);
    setUploadProgress("Uploading file to S3…");
    let historyId: string | null = null;

    try {
      if (file) {
        const resp = await userService.uploadUserImportFile(
          file, userType,
          isClientType && selectedClient ? selectedClient.id   : undefined,
          isClientType && selectedClient ? selectedClient.name : undefined,
        );
        historyId = resp.history_id;
      }

      setUploadProgress("Creating users…");

      const payload = {
        user_type: userType,
        client_id: isClientType && selectedClient ? selectedClient.id : undefined,
        users: validated.map((row) => ({
          email:            toSafeString(row["Email ID"]).toLowerCase(),
          first_name:       toSafeString(row["First Name"]),
          last_name:        toSafeString(row["Last Name"]),
          emp_id:           toSafeString(row["Emp ID"]),
          date_of_birth:    toSafeString(row["DOB"]),
          phone_number:     toSafeString(row["Phone Number"]) || undefined,
          role_names:       !isClientType && row["Role Name"]        ? toSafeString(row["Role Name"])                        : undefined,
          supervisor_email: !isClientType && row["Supervisor Email"] ? toSafeString(row["Supervisor Email"]).toLowerCase()   : undefined,
        })),
      };

      const result = await userService.bulkCreateUsers(payload);

      // Backend is now authoritative — use row_results directly
      const rowResults: RowResult[] = (result.row_results ?? []).map((r: any) => ({
        row_index: r.row_index,
        email:     r.email,
        status:    r.status,
        reason:    r.reason,
      }));

      const uploadRes: UploadResult = {
        created:    result.created  ?? 0,
        failed:     result.failed   ?? 0,
        mapped:     result.mapped   ?? 0,
        skipped:    result.skipped  ?? 0,
        rowResults,
        errors:     result.errors   ?? [],
      };

      if (historyId) {
        await userService.patchUserImportOutcome(historyId, {
          status:        uploadRes.failed > 0 && uploadRes.created === 0 ? "failed" : "completed",
          total_rows:    uploadRes.created + uploadRes.failed + uploadRes.skipped + uploadRes.mapped,
          success_count: uploadRes.created,
          failed_count:  uploadRes.failed,
          errors:        uploadRes.errors,
        });
      }

      const blob     = generateResultXLSX(validated, rowResults, isClientType);
      const filename = `upload_results_${new Date().toISOString().slice(0, 10)}.xlsx`;
      setResultBlob(blob);
      setResultFilename(filename);
      setUploadResult(uploadRes);
      setStep(doneStep);
      onSuccess({ created: uploadRes.created, failed: uploadRes.failed });

    } catch (err: any) {
      console.error("Bulk upload failed", err);
      if (historyId) {
        userService.patchUserImportOutcome(historyId, {
          status: "failed", total_rows: 0, success_count: 0,
          failed_count: validCount, errors: [err?.message ?? "Unknown error"],
        }).catch(() => {});
      }
      const blob     = generateResultXLSX(validated ?? [], [], isClientType);
      const filename = `upload_results_${new Date().toISOString().slice(0, 10)}.xlsx`;
      setResultBlob(blob);
      setResultFilename(filename);
      setUploadResult({ created: 0, failed: validCount, mapped: 0, skipped: 0, rowResults: [], errors: [err?.message || "Upload failed."] });
      setStep(doneStep);
    } finally {
      setUploading(false);
      setUploadProgress(null);
    }
  };

  const handleDownloadResult = () => {
    if (!resultBlob) return;
    const url = URL.createObjectURL(resultBlob);
    const a   = document.createElement("a");
    a.href = url; a.download = resultFilename; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 10_000);
  };

  const handleDownload = async (id: string) => {
    setDownloadingId(id);
    try {
      const res  = await userService.getUserImportDownloadUrl(id);
      const link = document.createElement("a");
      link.href = res.download_url; link.download = ""; link.click();
    } catch (err) { console.error("Download failed", err); }
    finally { setDownloadingId(null); }
  };

  const filteredClients = clients.filter(
    (c) => c.name.toLowerCase().includes(clientSearch.toLowerCase()) || (c.npi && c.npi.includes(clientSearch))
  );

  if (!isOpen) return null;

  // Derived counts from rowResults for done screen
  const failedRowsForDisplay = uploadResult?.rowResults.filter((r) => r.status === "failed") ?? [];

  return (
    <div className={styles.overlay}>
      <div className={styles.modal}>

        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <div className={styles.headerIcon}><Upload size={18} /></div>
            <div>
              <h2 className={styles.title}>Bulk User Upload</h2>
              <p className={styles.subtitle}>Import multiple users via CSV or Excel</p>
            </div>
          </div>
          <button className={styles.closeBtn} onClick={handleClose}><X size={18} /></button>
        </div>

        {/* Tab bar */}
        <div className={styles.tabBar}>
          <button className={`${styles.tabBtn} ${activeTab === "upload" ? styles.tabActive : ""}`} onClick={() => setActiveTab("upload")}>
            <Upload size={13} /> Upload
          </button>
          <button className={`${styles.tabBtn} ${activeTab === "history" ? styles.tabActive : ""}`} onClick={() => { setActiveTab("history"); loadHistory(); }}>
            <Clock size={13} /> History
          </button>
        </div>

        {/* Body */}
        <div className={styles.body}>
          {activeTab === "upload" && (
            <>
              {/* Step indicator */}
              {step < doneStep && (
                <div className={styles.stepBar}>
                  {steps.map((label, i) => {
                    const done = i < step, active = i === step;
                    return (
                      <React.Fragment key={i}>
                        <div className={`${styles.stepItem} ${active ? styles.stepActive : done ? styles.stepDone : ""}`}>
                          <div className={`${styles.stepDot} ${active ? styles.stepDotActive : done ? styles.stepDotDone : ""}`}>
                            {done ? <CheckCircle2 size={12} /> : <span>{i + 1}</span>}
                          </div>
                          <span className={styles.stepLabel}>{label}</span>
                        </div>
                        {i < steps.length - 1 && (
                          <div className={`${styles.stepConnector} ${i < step ? styles.stepConnectorDone : ""}`} />
                        )}
                      </React.Fragment>
                    );
                  })}
                </div>
              )}

              {/* STEP 0 — User type */}
              {step === 0 && (
                <div className={styles.section}>
                  <p className={styles.sectionDesc} style={{ marginTop: 8 }}>
                    Choose whether you're uploading internal staff or users linked to a specific client account.
                  </p>
                  <div className={styles.typeGrid}>
                    {(["internal", "client"] as const).map((type) => (
                      <button key={type} className={`${styles.typeCard} ${userType === type ? styles.typeCardSelected : ""}`} onClick={() => setUserType(type)}>
                        <div className={`${styles.typeCardIcon} ${userType === type ? styles.typeCardIconSelected : ""}`}>
                          {type === "internal" ? <Shield size={24} /> : <Building2 size={24} />}
                        </div>
                        <div className={styles.typeCardBody}>
                          <h3 className={styles.typeCardTitle}>{type === "internal" ? "Internal Users" : "Client Users"}</h3>
                          <p className={styles.typeCardDesc}>
                            {type === "internal"
                              ? "Staff, agents, supervisors or admins. Roles and supervisors can be set via file columns."
                              : "Users linked to a specific client. You'll choose the client next. CLIENT_ADMIN role is assigned automatically."}
                          </p>
                        </div>
                        <div className={`${styles.typeCardCheck} ${userType === type ? styles.typeCardCheckVisible : ""}`}>
                          <CheckCircle2 size={18} />
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* STEP 1 (client only) — Select client */}
              {isClientType && step === 1 && (
                <div className={styles.section}>
                  <p className={styles.sectionDesc}>All uploaded users will be linked to the selected client and assigned the <strong>CLIENT_ADMIN</strong> role.</p>
                  <div className={styles.searchWrap}>
                    <input className={styles.searchInput} placeholder="Search by client name or NPI..." value={clientSearch} onChange={(e) => setClientSearch(e.target.value)} autoFocus />
                  </div>
                  <div className={styles.clientList}>
                    {clientsLoading && <div className={styles.loadingRow}><Loader2 size={16} className={styles.spin} /><span>Loading clients...</span></div>}
                    {!clientsLoading && filteredClients.length === 0 && <div className={styles.emptyRow}>No clients found</div>}
                    {!clientsLoading && filteredClients.map((c) => (
                      <button key={c.id} className={`${styles.clientItem} ${selectedClient?.id === c.id ? styles.clientItemSelected : ""}`} onClick={() => setSelectedClient(c)}>
                        <div className={styles.clientItemLeft}>
                          <div className={styles.clientName}>{c.name}</div>
                          {c.npi && <div className={styles.clientNpi}>NPI: {c.npi}</div>}
                        </div>
                        <div className={styles.clientItemRight}>
                          {c.type && <span className={`${styles.typeBadge} ${c.type === "NPI2" ? styles.typeBadgeBlue : styles.typeBadgeAmber}`}>{c.type}</span>}
                          {selectedClient?.id === c.id && <CheckCircle2 size={16} className={styles.clientCheckIcon} />}
                        </div>
                      </button>
                    ))}
                  </div>
                  {selectedClient && (
                    <div className={styles.selectedClientBanner}>
                      <CheckCircle2 size={14} />
                      <span>Selected: <strong>{selectedClient.name}</strong></span>
                    </div>
                  )}
                </div>
              )}

              {/* Upload step */}
              {step === uploadStep && (
                <div className={styles.section}>
                  <div className={styles.uploadTopRow}>
                    <p className={styles.sectionDesc}>
                      Upload a .csv or .xlsx file. Required headers must be present; extra columns are ignored.
                      {isClientType && selectedClient && <span className={styles.clientTag}> Client: <strong>{selectedClient.name}</strong></span>}
                    </p>
                    <button className={styles.templateBtn} onClick={downloadTemplate}><Download size={13} /> Download Template</button>
                  </div>
                  <div
                    className={`${styles.dropzone} ${dragging ? styles.dropzoneActive : ""} ${file ? styles.dropzoneHasFile : ""}`}
                    onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                    onDragLeave={() => setDragging(false)}
                    onDrop={onDrop}
                    onClick={() => fileRef.current?.click()}
                  >
                    <input ref={fileRef} type="file" accept=".csv,.xlsx" style={{ display: "none" }} onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); }} />
                    <div className={styles.dropzoneIcon}>{file ? <FileText size={32} /> : <Upload size={32} />}</div>
                    <div className={styles.dropzoneText}>{file ? file.name : "Drop your file here or click to browse"}</div>
                    <div className={styles.dropzoneHint}>{file ? `${(file.size / 1024).toFixed(1)} KB · Click to replace` : "Accepts .csv and .xlsx files only"}</div>
                  </div>

                  {parsed?.error && <div className={styles.errorBox}><AlertCircle size={14} /><span>{parsed.error}</span></div>}
                  {!parsed?.error && invalidCount > 0 && (
                    <div className={styles.errorBox}><AlertCircle size={14} /><span>{invalidCount} invalid row{invalidCount !== 1 ? "s" : ""} will be skipped before proceeding.</span></div>
                  )}

                  {validated && !parsed?.error && (
                    <div className={styles.previewSection}>
                      <div className={styles.previewHeader}>
                        <span className={styles.previewTitle}>Preview — {validated.length} row{validated.length !== 1 ? "s" : ""}</span>
                        <div className={styles.badgeRow}>
                          <span className={styles.badgeGreen}><CheckCircle2 size={11} /> {validCount} valid</span>
                          {invalidCount > 0 && <span className={styles.badgeRed}><AlertCircle size={11} /> {invalidCount} invalid</span>}
                        </div>
                      </div>
                      <div className={styles.tableWrap}>
                        <table className={styles.previewTable}>
                          <thead>
                            <tr>
                              <th>#</th>
                              {(parsed?.headers ?? []).slice(0, 5).map((h) => <th key={h}>{h}</th>)}
                              <th>Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {validated.filter((r) => !r._valid).slice(0, 10).map((row, i) => (
                              <tr key={i} className={!row._valid ? styles.rowInvalid : ""}>
                                <td>{row._rowIndex}</td>
                                {(parsed?.headers ?? []).slice(0, 5).map((h) => (
                                  <td key={h} className={!row._valid && !row[h] ? styles.cellMissing : ""}>
                                    {row[h] || <span className={styles.emptyCell}>—</span>}
                                  </td>
                                ))}
                                <td>
                                  <span className={styles.badgeRed} title={row._errors.join(", ")}>
                                    <AlertCircle size={11} /> {row._errors.join(", ")}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {validated.length > 10 && <div className={styles.moreRows}>+{validated.length - 10} more rows not shown</div>}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Review step */}
              {step === reviewStep && validated && (
                <div className={styles.section}>
                  <p className={styles.sectionDesc}>Review the summary below. Only valid rows will be uploaded.</p>
                  <div className={styles.summaryCards}>
                    <div className={styles.summaryCard}>
                      <Users size={20} className={styles.summaryIcon} />
                      <div className={styles.summaryValue}>{validated.length}</div>
                      <div className={styles.summaryLabel}>Total Rows</div>
                    </div>
                    <div className={`${styles.summaryCard} ${styles.summaryCardGreen}`}>
                      <UserCheck size={20} className={styles.summaryIconGreen} />
                      <div className={styles.summaryValue}>{validCount}</div>
                      <div className={styles.summaryLabel}>Will Upload</div>
                    </div>
                    <div className={`${styles.summaryCard} ${invalidCount > 0 ? styles.summaryCardRed : ""}`}>
                      <UserX size={20} className={invalidCount > 0 ? styles.summaryIconRed : styles.summaryIcon} />
                      <div className={styles.summaryValue}>{invalidCount}</div>
                      <div className={styles.summaryLabel}>Will Skip</div>
                    </div>
                  </div>

                  <div className={styles.targetBox}>
                    <div className={styles.targetRow}>
                      <span className={styles.targetKey}>Type</span>
                      <span className={styles.targetVal}>
                        {isClientType
                          ? <><Building2 size={13} style={{ marginRight: 4, verticalAlign: "middle" }} />Client Users</>
                          : <><Shield size={13} style={{ marginRight: 4, verticalAlign: "middle" }} />Internal Users</>}
                      </span>
                    </div>
                    {isClientType && selectedClient && (
                      <div className={styles.targetRow}><span className={styles.targetKey}>Client</span><span className={styles.targetVal}>{selectedClient.name}</span></div>
                    )}
                    <div className={styles.targetRow}><span className={styles.targetKey}>File</span><span className={styles.targetVal}>{file?.name}</span></div>
                    {isClientType && (
                      <div className={styles.targetRow}><span className={styles.targetKey}>Auto Role</span><span className={`${styles.targetVal} ${styles.targetValAccent}`}>CLIENT_ADMIN</span></div>
                    )}
                  </div>

                  {invalidCount > 0 && (
                    <div className={styles.warningBox}>
                      <AlertCircle size={14} />
                      <div>
                        <strong>{invalidCount} row{invalidCount !== 1 ? "s" : ""} will be skipped</strong>
                        <div className={styles.warningList}>
                          {validated.filter((r) => !r._valid).slice(0, 4).map((r, i) => (
                            <div key={i} className={styles.warningItem}>Row {r._rowIndex} ({r.email || "no email"}): {r._errors.join(", ")}</div>
                          ))}
                          {invalidCount > 4 && <div className={styles.warningItem}>…and {invalidCount - 4} more</div>}
                        </div>
                      </div>
                    </div>
                  )}

                  {uploading && (
                    <div className={styles.uploadingRow}>
                      <Loader2 size={16} className={styles.spin} />
                      <span>{uploadProgress ?? `Uploading ${validCount} users…`}</span>
                    </div>
                  )}
                </div>
              )}

              {/* Done step */}
              {step === doneStep && uploadResult && (
                <div className={styles.doneSection}>
                  <div className={`${styles.doneIcon} ${uploadResult.failed === 0 ? styles.doneIconSuccess : styles.doneIconWarning}`}>
                    {uploadResult.failed === 0 ? <CheckCircle2 size={40} /> : <AlertCircle size={40} />}
                  </div>
                  <h3 className={styles.doneTitle}>{uploadResult.failed === 0 ? "All users uploaded!" : "Upload complete"}</h3>
                  <p className={styles.doneSubtitle}>
                    {uploadResult.created} user{uploadResult.created !== 1 ? "s" : ""} created
                    {uploadResult.mapped  > 0 ? `, ${uploadResult.mapped} supervisor mapped`  : ""}
                    {uploadResult.skipped > 0 ? `, ${uploadResult.skipped} skipped`           : ""}
                    {uploadResult.failed  > 0 ? `, ${uploadResult.failed} failed`             : ""}.
                  </p>

                  <div className={styles.doneSummaryCards}>
                    <div className={`${styles.summaryCard} ${styles.summaryCardGreen}`}>
                      <UserCheck size={20} className={styles.summaryIconGreen} />
                      <div className={styles.summaryValue}>{uploadResult.created}</div>
                      <div className={styles.summaryLabel}>Created</div>
                    </div>
                    {uploadResult.mapped > 0 && (
                      <div className={styles.summaryCard}>
                        <Users size={20} className={styles.summaryIcon} />
                        <div className={styles.summaryValue}>{uploadResult.mapped}</div>
                        <div className={styles.summaryLabel}>Mapped</div>
                      </div>
                    )}
                    {(uploadResult.failed > 0 || uploadResult.skipped > 0) && (
                      <div className={`${styles.summaryCard} ${styles.summaryCardRed}`}>
                        <UserX size={20} className={styles.summaryIconRed} />
                        <div className={styles.summaryValue}>{uploadResult.failed + uploadResult.skipped}</div>
                        <div className={styles.summaryLabel}>Failed / Skipped</div>
                      </div>
                    )}
                  </div>

                  {uploadResult.errors.length > 0 && (
                    <div className={styles.errorBox} style={{ marginTop: 16 }}>
                      <AlertCircle size={14} /><span>{uploadResult.errors[0]}</span>
                    </div>
                  )}

                  {failedRowsForDisplay.length > 0 && (
                    <div className={styles.warningBox} style={{ marginTop: 12 }}>
                      <AlertCircle size={14} />
                      <div>
                        <strong>Failed rows</strong>
                        <div className={styles.warningList}>
                          {failedRowsForDisplay.slice(0, 5).map((r, i) => (
                            <div key={i} className={styles.warningItem}>{r.email || `Row ${r.row_index}`}: {r.reason}</div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 16, alignItems: "center" }}>
                    {resultBlob && (
                      <button className={styles.templateBtn} onClick={handleDownloadResult}>
                        <Download size={13} /> Download Results (.xlsx)
                      </button>
                    )}
                    <button className={styles.viewHistoryBtn} onClick={() => { setActiveTab("history"); loadHistory(); }}>
                      <Clock size={14} /> View in history
                    </button>
                  </div>
                </div>
              )}
            </>
          )}

          {/* History tab */}
          {activeTab === "history" && (
            <div className={styles.historyPanel}>
              <div className={styles.historyToolbar}>
                <span className={styles.historyCount}>{historyTotal} import{historyTotal !== 1 ? "s" : ""}</span>
                <button className={styles.refreshBtn} onClick={loadHistory} disabled={historyLoading}>
                  <RefreshCw size={14} className={historyLoading ? styles.spinning : ""} /> Refresh
                </button>
              </div>
              {historyError && <div className={styles.errorBox} style={{ marginBottom: 12 }}><AlertCircle size={15} /><span>{historyError}</span></div>}
              {historyLoading && !history.length ? (
                <div className={styles.historyEmpty}>Loading…</div>
              ) : !history.length ? (
                <div className={styles.historyEmpty}>
                  <Clock size={32} style={{ opacity: 0.3, marginBottom: 8 }} />
                  <p>No imports yet. Start your first upload from the Upload tab.</p>
                </div>
              ) : (
                <div className={styles.historyList}>
                  {history.map((item) => {
                    const meta = STATUS_META[item.status] ?? STATUS_META.pending;
                    const isExpanded = expandedId === item.id;
                    return (
                      <div key={item.id} className={styles.historyCard}>
                        <div className={styles.historyCardHeader} onClick={() => setExpandedId(isExpanded ? null : item.id)}>
                          <div className={styles.historyCardLeft}>
                            <FileText size={16} className={styles.historyFileIcon} />
                            <div>
                              <span style={{ color: "#2563eb", cursor: "pointer", textDecoration: "underline" }} onClick={(e) => { e.stopPropagation(); handleDownload(item.id); }}>
                                {item.original_filename}
                              </span>
                              <div className={styles.historyMeta}>
                                {item.user_type === "client" ? "Client" : "Internal"}
                                {item.client_name ? ` · ${item.client_name}` : ""}{" · "}
                                {fmtBytes(item.file_size_bytes)}{" · "}{fmtDate(item.created_at)}
                              </div>
                            </div>
                          </div>
                          <div className={styles.historyCardRight}>
                            <span className={styles.statusBadge} style={{ color: meta.color, borderColor: meta.color }}>{meta.label}</span>
                            {item.s3_key && (
                              <button onClick={(e) => { e.stopPropagation(); handleDownload(item.id); }} disabled={downloadingId === item.id}
                                style={{ marginLeft: 8, cursor: "pointer", border: "none", background: "transparent" }} title="Download original file">
                                {downloadingId === item.id ? <Loader2 size={14} className={styles.spin} /> : <Download size={16} />}
                              </button>
                            )}
                            <ChevronRight size={14} className={`${styles.expandChevron} ${isExpanded ? styles.expanded : ""}`} />
                          </div>
                        </div>
                        {isExpanded && (
                          <div className={styles.historyCardBody}>
                            {item.total_rows != null && (
                              <div className={styles.outcomeSummary}>
                                <span className={styles.outcomeSuccess}>✓ {item.success_count ?? 0} succeeded</span>
                                <span className={styles.outcomeFailed}>✗ {item.failed_count ?? 0} failed</span>
                                <span className={styles.outcomeTotal}>of {item.total_rows} total</span>
                              </div>
                            )}
                            {item.errors && item.errors.length > 0 && (
                              <div className={styles.historyErrorList}>
                                <p className={styles.errorListTitle}>Errors / warnings:</p>
                                <ul>
                                  {item.errors.slice(0, 20).map((e, i) => <li key={i}>{e}</li>)}
                                  {item.errors.length > 20 && <li className={styles.moreErrors}>… and {item.errors.length - 20} more</li>}
                                </ul>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className={styles.footer}>
          <div>
            {activeTab === "history" || step === doneStep ? (
              <button className={styles.btnSecondary} onClick={handleClose}>Close</button>
            ) : step > 0 ? (
              <button className={styles.btnSecondary} onClick={goBack} disabled={uploading}><ChevronLeft size={15} /> Back</button>
            ) : (
              <button className={styles.btnSecondary} onClick={handleClose}>Cancel</button>
            )}
          </div>
          <div className={styles.footerRight}>
            {activeTab === "upload" && (
              <>
                {step === 0 && (
                  <button className={styles.btnPrimary} disabled={!userType} onClick={goNext}>Continue <ChevronRight size={15} /></button>
                )}
                {isClientType && step === 1 && (
                  <button className={styles.btnPrimary} disabled={!selectedClient} onClick={goNext}>Continue <ChevronRight size={15} /></button>
                )}
                {step === uploadStep && (
                  <button className={styles.btnPrimary} disabled={!validated || !!parsed?.error || validCount === 0 || invalidCount > 0} onClick={goNext}>
                    Review ({validCount}) <ChevronRight size={15} />
                  </button>
                )}
                {step === reviewStep && (
                  <button className={styles.btnSuccess} disabled={uploading || validCount === 0} onClick={handleSubmit}>
                    {uploading
                      ? <><Loader2 size={14} className={styles.spin} /> {uploadProgress ?? "Uploading…"}</>
                      : <><Upload size={14} /> Upload {validCount} User{validCount !== 1 ? "s" : ""}</>}
                  </button>
                )}
                {step === doneStep && (
                  <button className={styles.btnPrimary} onClick={resetUpload}>Upload Another</button>
                )}
              </>
            )}
          </div>
        </div>

      </div>
    </div>
  );
};

export default BulkUserUploadModal;